import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../utils/api';
import { CheckCircle, Loader2, Smartphone, ShieldAlert } from 'lucide-react';

const STORAGE_KEY = 'qr_device_token';

export default function QRLogin({ onLogin }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const userId = params.get('user');
  const sig = params.get('sig');

  const [state, setState] = useState('loading'); // loading | password | success | error
  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!userId || !sig) {
      setState('error');
      setErrorMsg('Invalid QR link — missing parameters.');
      return;
    }
    attemptLogin();
  }, []);

  const attemptLogin = async () => {
    setState('loading');
    const storedToken = localStorage.getItem(STORAGE_KEY);
    try {
      const res = await api.post('/auth/qr-login', {
        user_id: userId,
        sig: sig,
        device_token: storedToken || '',
      });
      if (res.data.authenticated) {
        // Instant login — token was valid
        localStorage.setItem('token', res.data.access_token);
        if (res.data.session_id) localStorage.setItem('session_id', res.data.session_id);
        setState('success');
        toast.success('Logged in!');
        if (onLogin) {
          onLogin(res.data.user, res.data.access_token);
        }
        setTimeout(() => navigate('/'), 1000);
      } else if (res.data.needs_password) {
        setUserName(res.data.user_name || '');
        setState('password');
      }
    } catch (err) {
      setState('error');
      setErrorMsg(err?.response?.data?.detail || 'Authentication failed');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Enter email and password'); return; }
    setLoading(true);
    try {
      const ua = navigator.userAgent;
      let deviceLabel = 'Phone';
      if (/iPhone/.test(ua)) deviceLabel = 'iPhone';
      else if (/Android/.test(ua)) deviceLabel = 'Android';
      else if (/iPad/.test(ua)) deviceLabel = 'iPad';
      const now = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      deviceLabel += ` - added ${now}`;

      const res = await api.post('/auth/qr-login-password', {
        user_id: userId,
        sig: sig,
        email,
        password,
        device_label: deviceLabel,
      });
      if (res.data.authenticated) {
        // Store device token for future scans
        if (res.data.device_token) {
          localStorage.setItem(STORAGE_KEY, res.data.device_token);
        }
        localStorage.setItem('token', res.data.access_token);
        if (res.data.session_id) localStorage.setItem('session_id', res.data.session_id);
        setState('success');
        toast.success('Device registered & logged in!');
        if (onLogin) {
          onLogin(res.data.user, res.data.access_token);
        }
        setTimeout(() => navigate('/'), 1000);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6" style={{ background: 'var(--bg-void)' }}>
        <img src="/workforce-logo.png" alt="WorkForce" style={{ height: '64px' }} />
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--amber)' }} />
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Authenticating...</p>
      </div>
    );
  }

  if (state === 'success') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6" style={{ background: 'var(--bg-void)' }}>
        <CheckCircle className="h-16 w-16" style={{ color: 'var(--green)' }} />
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fd)' }}>Logged In</h1>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Redirecting to dashboard...</p>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6" style={{ background: 'var(--bg-void)' }}>
        <ShieldAlert className="h-12 w-12" style={{ color: 'var(--red)' }} />
        <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fd)' }}>Authentication Failed</h1>
        <p className="text-sm text-center" style={{ color: 'var(--text-muted)' }}>{errorMsg}</p>
      </div>
    );
  }

  // state === 'password'
  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--bg-void)' }}>
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <Smartphone className="h-10 w-10 mx-auto mb-3" style={{ color: 'var(--amber)' }} />
          <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fd)' }}>New Device</h1>
          {userName && <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Hi {userName}, verify your identity to register this device</p>}
          {!userName && <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Sign in to register this device for quick login</p>}
        </div>
        <form onSubmit={handlePasswordSubmit} className="space-y-4 rounded-xl p-6" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}>
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)', fontFamily: 'var(--fd)' }}>Email</label>
            <Input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com" className="h-11"
              style={{ background: 'var(--bg-void)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              data-testid="qr-email" autoFocus
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)', fontFamily: 'var(--fd)' }}>Password</label>
            <Input
              type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Password" className="h-11"
              style={{ background: 'var(--bg-void)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              data-testid="qr-password"
            />
          </div>
          <Button
            type="submit" disabled={loading}
            className="w-full h-12 font-bold uppercase tracking-wider"
            style={{ background: 'var(--amber)', color: 'var(--bg-void)', fontFamily: 'var(--fd)' }}
            data-testid="qr-submit"
          >
            {loading ? 'Registering device...' : 'Sign In & Register Device'}
          </Button>
        </form>
      </div>
    </div>
  );
}
